/**
 * Plain Language Translations for Admin Analytics
 * 
 * Converts technical behavioral science terms to everyday Indonesian
 * that supervisors and HSE officers can understand immediately.
 */

// Hazard category translations with detailed descriptions
export const hazardCategoryTranslations: Record<string, { name: string; example: string }> = {
  no_ppe: { name: "Tidak pakai APD", example: "tidak pakai helm, sarung tangan, kacamata las" },
  improper_lifting: { name: "Mengangkat tidak benar", example: "angkat barang berat dengan posisi salah" },
  unsafe_ladder: { name: "Tangga tidak aman", example: "naik tangga rusak atau posisi tidak stabil" },
  electrical_hazard: { name: "Bahaya listrik", example: "kabel terbuka, colokan rusak, panel tidak ditutup" },
  slip_trip_fall: { name: "Terpeleset/tersandung", example: "lantai licin, kabel melintang, barang berserakan" },
  chemical_exposure: { name: "Paparan bahan kimia", example: "tidak pakai masker saat ada bau kimia" },
  equipment_defect: { name: "Alat rusak", example: "mesin tidak berfungsi dengan benar" },
  fire_risk: { name: "Risiko kebakaran", example: "merokok di area terlarang, alat las dekat bahan mudah terbakar" },
  speeding: { name: "Ngebut", example: "mengemudi kendaraan melebihi batas kecepatan" },
  fatigue: { name: "Kelelahan", example: "bekerja dalam kondisi sangat lelah atau kurang tidur" },
  housekeeping: { name: "Kebersihan area kerja", example: "area kerja berantakan, sampah tidak dibuang" },
  confined_space: { name: "Ruang terbatas", example: "masuk ruang sempit tanpa prosedur keselamatan" },
  working_at_height: { name: "Bekerja di ketinggian", example: "tidak pakai body harness saat di atas" },
  ppe: { name: "APD (Alat Pelindung Diri)", example: "tidak menggunakan APD yang diwajibkan" },
  unsafe_act: { name: "Tindakan Tidak Aman", example: "melakukan pekerjaan dengan cara berbahaya" },
  unsafe_condition: { name: "Kondisi Tidak Aman", example: "kondisi lingkungan kerja yang berbahaya" },
  near_miss: { name: "Hampir Celaka", example: "kejadian yang hampir menyebabkan cedera" },
  slips_trips_falls: { name: "Terpeleset/Tersandung/Jatuh", example: "lantai licin atau kabel menghalangi" },
  electrical: { name: "Bahaya Listrik", example: "kabel rusak atau panel tidak aman" },
  fire: { name: "Bahaya Kebakaran", example: "sumber api di dekat bahan mudah terbakar" },
  chemical: { name: "Bahaya Kimia", example: "paparan zat kimia berbahaya" },
  ergonomic: { name: "Ergonomi", example: "posisi kerja yang menyebabkan kelelahan" },
  machinery: { name: "Mesin & Peralatan", example: "mesin tanpa pelindung atau rusak" },
  vehicle: { name: "Kendaraan", example: "penggunaan kendaraan tidak aman" },
  noise: { name: "Kebisingan", example: "tingkat kebisingan yang berbahaya" },
  heat_stress: { name: "Stres Panas", example: "bekerja di lingkungan panas berlebihan" },
  manual_handling: { name: "Penanganan Manual", example: "mengangkat atau memindahkan barang secara tidak benar" },
  other: { name: "Lainnya", example: "bahaya lain yang tidak termasuk kategori di atas" },
};

// Simple string translations for impact levels
export const impactTranslations: Record<string, string> = {
  high: "Dampak Tinggi",
  medium: "Dampak Sedang",
  low: "Dampak Rendah",
};

// Simple string translations for shift times
export const shiftTranslations: Record<string, string> = {
  morning: "Pagi",
  afternoon: "Siang",
  night: "Malam",
  day: "Siang",
  evening: "Sore",
};

// Simple string translations for friction causes
export const frictionCauseTranslations: Record<string, string> = {
  equipment: "Masalah peralatan",
  time_pressure: "Tekanan waktu / deadline",
  training: "Kurang pelatihan",
  environment: "Kondisi lingkungan kerja",
  ppe: "APD tidak tersedia / tidak nyaman",
  procedure: "Prosedur tidak jelas",
  fatigue: "Kelelahan",
  communication: "Komunikasi kurang",
  supervision: "Pengawasan kurang",
};

// Network role explanations in plain language
export const networkRoleTranslations: Record<string, { 
  title: string; 
  impact: string; 
  recommendation: string 
}> = {
  wide_bridge: {
    title: "Punya banyak teman di berbagai tim",
    impact: "Jika dia punya kebiasaan buruk, bisa cepat menular ke banyak orang di tim lain",
    recommendation: "Prioritas intervensi - perubahan perilakunya berdampak luas"
  },
  champion: {
    title: "Punya pengaruh besar ke rekan kerja",
    impact: "Orang-orang sering meniru apa yang dia lakukan",
    recommendation: "Bisa dijadikan contoh positif jika sudah aman"
  },
  bridge: {
    title: "Penghubung antar kelompok",
    impact: "Menyebarkan informasi dan kebiasaan antar tim",
    recommendation: "Perlu monitoring perilakunya"
  },
  isolate: {
    title: "Jarang bergaul dengan rekan kerja",
    impact: "Kurang terpengaruh oleh norma kelompok",
    recommendation: "Butuh pendekatan personal, bukan lewat tekanan sosial"
  },
  member: {
    title: "Anggota tim biasa",
    impact: "Mengikuti kebiasaan mayoritas tim",
    recommendation: "Intervensi lewat kelompok lebih efektif"
  }
};

// Psychometric trait explanations in plain language
export const psychometricTranslations: Record<string, {
  title: string;
  meaning: string;
  workRisk: string;
}> = {
  sensation_seeking: {
    title: "Suka tantangan dan sensasi",
    meaning: "Tipe orang yang mencari pengalaman baru dan menarik",
    workRisk: "Mungkin mengambil jalan pintas yang berbahaya demi sensasi"
  },
  negative_urgency: {
    title: "Mudah gegabah saat emosi",
    meaning: "Cenderung bertindak tanpa pikir panjang saat marah, stres, atau kesal",
    workRisk: "Berisiko ceroboh saat ada tekanan atau masalah"
  },
  lack_of_perseverance: {
    title: "Cepat menyerah saat sulit",
    meaning: "Sulit fokus menyelesaikan tugas yang membutuhkan ketelitian",
    workRisk: "Mungkin melewatkan langkah keselamatan yang 'ribet'"
  },
  lack_of_premeditation: {
    title: "Sering bertindak tanpa rencana",
    meaning: "Jarang memikirkan konsekuensi sebelum bertindak",
    workRisk: "Berisiko tidak mengikuti prosedur yang sudah ditetapkan"
  },
  positive_urgency: {
    title: "Impulsif saat senang",
    meaning: "Bertindak ceroboh saat dalam mood baik",
    workRisk: "Mungkin terlalu percaya diri dan mengabaikan risiko"
  },
  dospert_health_safety: {
    title: "Toleransi risiko kesehatan tinggi",
    meaning: "Merasa nyaman mengambil risiko yang mempengaruhi keselamatan",
    workRisk: "Cenderung meremehkan bahaya di tempat kerja"
  },
  delay_discounting: {
    title: "Fokus pada keuntungan jangka pendek",
    meaning: "Lebih memilih reward sekarang daripada manfaat di masa depan",
    workRisk: "Mungkin melewatkan prosedur demi cepat selesai"
  }
};

// Safety state explanations in plain language
export const safetyStateTranslations: Record<string, {
  title: string;
  meaning: string;
  action: string;
}> = {
  safe: {
    title: "Aman",
    meaning: "Tidak ada masalah keselamatan yang terdeteksi",
    action: "Lanjutkan monitoring rutin"
  },
  at_risk: {
    title: "Perlu Perhatian",
    meaning: "Ada beberapa tanda peringatan yang mulai muncul",
    action: "Berikan pengingat sebelum masalah membesar"
  },
  high_risk: {
    title: "Butuh Tindakan Segera",
    meaning: "Sudah ada beberapa insiden atau pelanggaran",
    action: "Perlu intervensi aktif dari supervisor"
  },
  chronic: {
    title: "Masalah Berulang",
    meaning: "Pelanggaran terus terjadi meskipun sudah ditegur",
    action: "Butuh pendekatan khusus dan monitoring ketat"
  }
};

// Intervention type explanations
export const interventionTypeTranslations: Record<string, {
  title: string;
  description: string;
}> = {
  norm_nudge_popup: { 
    title: "Pengingat Norma Tim", 
    description: "Menunjukkan bahwa rekan kerja lain sudah bekerja dengan aman" 
  },
  success_story_popup: { 
    title: "Cerita Sukses", 
    description: "Berbagi kisah rekan yang berhasil bekerja aman" 
  },
  recognition_popup: { 
    title: "Apresiasi", 
    description: "Memberikan pengakuan atas perilaku aman" 
  },
  commitment_quest: { 
    title: "Komitmen Pribadi", 
    description: "Meminta janji untuk melakukan hal spesifik" 
  },
  video_challenge_quest: { 
    title: "Tantangan Video", 
    description: "Merekam diri melakukan prosedur dengan benar" 
  },
  peer_observation_quest: { 
    title: "Observasi Teman", 
    description: "Mengamati dan memberikan feedback ke rekan" 
  },
  cluster_challenge_quest: { 
    title: "Tantangan Tim", 
    description: "Kompetisi keselamatan antar tim" 
  },
  micro_learning_quest: { 
    title: "Pelatihan Singkat", 
    description: "Materi pembelajaran 2-3 menit tentang topik spesifik" 
  },
  supervisor_meeting: { 
    title: "Diskusi dengan Atasan", 
    description: "Percakapan langsung untuk memahami masalah" 
  },
  loss_frame_popup: { 
    title: "Pengingat Risiko", 
    description: "Menjelaskan apa yang bisa hilang jika tidak hati-hati" 
  }
};

/**
 * Generate a detailed plain language summary for a friction point
 */
export function generateFrictionSummary(point: {
  location: string;
  hazard_category: string;
  report_count: number;
  friction_score: number;
  estimated_impact?: string | null;
  peak_shift?: string | null;
  affected_worker_count?: number | null;
  primary_cause?: string | null;
  cause_distribution?: any;
  example_details?: string[] | null;
  suggested_fix?: string | null;
}): string[] {
  const points: string[] = [];
  
  // Report count with context
  points.push(`${point.report_count} laporan masalah serupa dalam 90 hari terakhir`);
  
  // Peak shift if available
  if (point.peak_shift) {
    const shiftName = shiftTranslations[point.peak_shift] || point.peak_shift;
    points.push(`Paling sering terjadi saat shift ${shiftName}`);
  }
  
  // Affected workers
  if (point.affected_worker_count && point.affected_worker_count > 0) {
    points.push(`${point.affected_worker_count} pekerja terdampak`);
  }
  
  // Primary cause with plain language
  if (point.primary_cause) {
    const causeName = frictionCauseTranslations[point.primary_cause] || point.primary_cause.replace(/_/g, ' ');
    points.push(`Penyebab utama: ${causeName}`);
  }
  
  // Example from workers
  if (point.example_details && point.example_details.length > 0) {
    const example = point.example_details[0].slice(0, 80);
    points.push(`Contoh keluhan pekerja: "${example}..."`);
  }
  
  return points;
}

/**
 * Generate a detailed plain language summary for a worker
 */
export function generateWorkerSummary(worker: {
  display_name: string;
  violation_count: number;
  violations: Array<{ hazard_category: string; description?: string; created_at: string }>;
  is_wide_bridge: boolean;
  network_role: string | null;
  risky_neighbor_count: number;
  psychometric_profile: Record<string, any> | null;
  safety_state: string;
  current_risk_score: number;
}): string[] {
  const points: string[] = [];
  
  // Violation details - be specific about what they did
  if (worker.violation_count > 0 && worker.violations.length > 0) {
    const violationDetails = worker.violations.slice(0, 3).map(v => {
      const category = hazardCategoryTranslations[v.hazard_category];
      if (category) {
        return category.name.toLowerCase();
      }
      return v.hazard_category.replace(/_/g, ' ');
    });
    
    const uniqueViolations = [...new Set(violationDetails)];
    const timeframe = "30 hari terakhir";
    
    if (uniqueViolations.length === 1) {
      points.push(`Dilaporkan ${worker.violation_count} kali karena ${uniqueViolations[0]} dalam ${timeframe}`);
    } else {
      points.push(`Dilaporkan ${worker.violation_count} kali dalam ${timeframe} karena: ${uniqueViolations.join(', ')}`);
    }
  }
  
  // Network role impact - explain why it matters
  if (worker.is_wide_bridge) {
    const role = networkRoleTranslations.wide_bridge;
    points.push(`${role.title} - ${role.impact.toLowerCase()}`);
  } else if (worker.network_role && networkRoleTranslations[worker.network_role]) {
    const role = networkRoleTranslations[worker.network_role];
    if (worker.network_role === 'champion' || worker.network_role === 'isolate') {
      points.push(`${role.title} - ${role.impact.toLowerCase()}`);
    }
  }
  
  // Risky neighbors - concrete impact
  if (worker.risky_neighbor_count > 0) {
    points.push(`Sering bekerja bersama ${worker.risky_neighbor_count} rekan yang juga bermasalah - bisa saling mempengaruhi`);
  }
  
  // Psychometric factors - translate to behavior
  const psychProfile = worker.psychometric_profile;
  if (psychProfile) {
    const upps = psychProfile.upps_p || {};
    const significantTraits: string[] = [];
    
    if ((upps.sensation_seeking || 0) > 0.5) {
      significantTraits.push("suka tantangan");
    }
    if ((upps.negative_urgency || 0) > 0.5) {
      significantTraits.push("mudah gegabah saat tertekan");
    }
    if ((upps.lack_of_perseverance || 0) > 0.5) {
      significantTraits.push("cepat bosan dengan prosedur rutin");
    }
    if ((upps.lack_of_premeditation || 0) > 0.5) {
      significantTraits.push("sering bertindak tanpa rencana");
    }
    if ((psychProfile.dospert_health_safety || 0) > 0.5) {
      significantTraits.push("cenderung meremehkan risiko");
    }
    
    if (significantTraits.length > 0) {
      points.push(`Dari kepribadian: ${significantTraits.join(', ')}`);
    }
  }
  
  // If no significant factors
  if (points.length === 0) {
    points.push("Tidak ada faktor risiko signifikan yang terdeteksi saat ini");
  }
  
  return points;
}

/**
 * Generate a detailed plain language summary for a cluster
 */
export function generateClusterSummary(cluster: {
  cluster_name: string | null;
  risk_score: number;
  member_count: number;
  infected_count: number;
  wide_bridges: Array<{ display_name: string }>;
  members: Array<{ safety_state: string }>;
  redundancy_score: number;
}): string[] {
  const points: string[] = [];
  
  // At-risk members
  const chronicCount = cluster.members.filter(m => m.safety_state === 'chronic').length;
  const highRiskCount = cluster.members.filter(m => m.safety_state === 'high_risk').length;
  const atRiskCount = cluster.members.filter(m => m.safety_state === 'at_risk').length;
  
  if (chronicCount > 0) {
    points.push(`Ada ${chronicCount} anggota yang masalahnya sudah berulang-ulang - perlu perhatian khusus`);
  }
  if (highRiskCount > 0) {
    points.push(`${highRiskCount} anggota butuh tindakan segera karena sudah ada beberapa insiden`);
  }
  if (atRiskCount > 0) {
    points.push(`${atRiskCount} anggota mulai menunjukkan tanda-tanda perlu perhatian`);
  }
  
  // Wide bridges
  if (cluster.wide_bridges.length > 0) {
    const bridgeNames = cluster.wide_bridges.slice(0, 2).map(b => b.display_name).join(' dan ');
    if (cluster.wide_bridges.length === 1) {
      points.push(`${bridgeNames} punya banyak teman di tim lain - jika kebiasaan buruknya tidak diperbaiki, bisa menular ke tim lain`);
    } else {
      points.push(`${bridgeNames} punya banyak koneksi ke tim lain - kebiasaan mereka bisa menyebar luas`);
    }
  }
  
  // Risky neighbor exposure
  if (cluster.redundancy_score > 0.5) {
    points.push(`Tim ini sering terpapar pengaruh dari anggota bermasalah - efek negatif bisa menyebar cepat`);
  }
  
  // Overall status
  if (points.length === 0) {
    if (cluster.risk_score < 30) {
      points.push("Tim ini dalam kondisi baik - semua anggota bekerja dengan aman");
    } else {
      points.push("Tim ini perlu monitoring rutin untuk menjaga kondisi tetap stabil");
    }
  }
  
  return points;
}

/**
 * Generate a detailed plain language explanation for an intervention recommendation
 */
export function generateRecommendationSummary(suggestion: {
  worker_name?: string;
  cluster_name?: string;
  target_type: 'individual' | 'cluster';
  intervention_type: string;
  worker_safety_state?: string;
  worker_risk_score?: number;
  cluster_risk_score?: number;
  cluster_member_count?: number;
  trigger_reason?: string;
  mdp_analysis?: {
    mdp_state?: { current: string; violations_30d?: number };
    violation_context?: { count: number; types: string[]; recent_items?: Array<{ category: string; description: string }> };
    network_context?: { role: string; is_wide_bridge: boolean };
    psychometric_factors?: string[];
    selected_action?: { reasoning: string };
  } | null;
}): { whatToDo: string; whyThisWorker: string[]; whyThisIntervention: string } {
  const isCluster = suggestion.target_type === 'cluster';
  const name = isCluster ? suggestion.cluster_name : suggestion.worker_name;
  const intervention = interventionTypeTranslations[suggestion.intervention_type] || {
    title: suggestion.intervention_type.replace(/_/g, ' '),
    description: 'Intervensi keselamatan'
  };
  
  // What to do - plain language action
  const whatToDo = `${intervention.title} untuk ${name}`;
  
  // Why this worker/cluster
  const whyThisWorker: string[] = [];
  
  if (isCluster) {
    if (suggestion.cluster_risk_score !== undefined && suggestion.cluster_risk_score >= 40) {
      whyThisWorker.push(`Tim ini punya skor risiko ${suggestion.cluster_risk_score}%`);
    }
    if (suggestion.cluster_member_count) {
      whyThisWorker.push(`${suggestion.cluster_member_count} anggota tim perlu perhatian bersama`);
    }
  } else {
    // Add violation details if available
    if (suggestion.mdp_analysis?.violation_context?.recent_items?.length) {
      const violations = suggestion.mdp_analysis.violation_context.recent_items.slice(0, 2);
      const violationList = violations.map(v => {
        const cat = hazardCategoryTranslations[v.category];
        return v.description || (cat ? cat.name : v.category);
      }).join(', ');
      whyThisWorker.push(`Dilaporkan karena: ${violationList}`);
    } else if (suggestion.mdp_analysis?.violation_context?.count) {
      whyThisWorker.push(`${suggestion.mdp_analysis.violation_context.count} laporan pelanggaran baru-baru ini`);
    }
    
    // Safety state
    if (suggestion.worker_safety_state) {
      const stateInfo = safetyStateTranslations[suggestion.worker_safety_state];
      if (stateInfo) {
        whyThisWorker.push(stateInfo.meaning);
      }
    }
    
    // Network role
    if (suggestion.mdp_analysis?.network_context?.is_wide_bridge) {
      const role = networkRoleTranslations['wide_bridge'];
      whyThisWorker.push(`${role.title} - kebiasaannya bisa menular`);
    } else if (suggestion.mdp_analysis?.network_context?.role) {
      const roleKey = suggestion.mdp_analysis.network_context.role;
      const role = networkRoleTranslations[roleKey];
      if (role) {
        whyThisWorker.push(role.impact);
      }
    }
    
    // Psychometric factors
    if (suggestion.mdp_analysis?.psychometric_factors?.length) {
      const factor = suggestion.mdp_analysis.psychometric_factors[0];
      const factorKey = factor.toLowerCase().replace(/ /g, '_');
      const trait = psychometricTranslations[factorKey];
      if (trait) {
        whyThisWorker.push(`Dari kepribadian: ${trait.workRisk}`);
      }
    }
  }
  
  // Why this intervention type
  const whyThisIntervention = intervention.description || 
    suggestion.mdp_analysis?.selected_action?.reasoning || 
    suggestion.trigger_reason || 
    'Cocok dengan profil dan situasinya';
  
  return { whatToDo, whyThisWorker, whyThisIntervention };
}

// Legacy alias for backward compatibility
export const generateRecommendationExplanation = (suggestion: Parameters<typeof generateRecommendationSummary>[0]) => {
  const result = generateRecommendationSummary(suggestion);
  return {
    what: result.whatToDo,
    why: result.whyThisWorker.join('. '),
    how: result.whyThisIntervention
  };
};
